import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Statements() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📜 JavaScript Statements</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        JavaScript <b>statements</b> are instructions that the browser can
        execute. Each statement performs an action — such as assigning a value,
        displaying text, or performing a calculation. Programs are made up of
        multiple statements executed in sequence.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>JavaScript programs are made of statements.</li>
          <li>
            Each statement ends with a semicolon (<code>;</code>).
          </li>
          <li>
            Multiple statements form a code block using <code>{`{}`}</code>.
          </li>
          <li>Statements are executed top to bottom, left to right.</li>
          <li>Whitespace and line breaks do not affect execution.</li>
        </ul>
      </div>

      {/* Example 1: Basic Statements */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic Statements</h2>
        <p>
          Each instruction below is a separate statement. They execute in
          sequence.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = 5;
let b = 10;
let sum = a + b;
console.log(sum);`}
          />
          <TryItButton
            code={`let a = 5;
let b = 10;
let sum = a + b;
console.log(sum);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output: <b>15</b>
        </div>
      </div>

      {/* Example 2: Multiple Statements on One Line */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Multiple Statements on One Line</h2>
        <p>
          You can write multiple statements on one line using semicolons (
          <code>;</code>), but it’s not recommended for readability.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 3; let y = 4; let result = x * y; console.log(result);`}
          />
          <TryItButton
            code={`let x = 3; let y = 4; let result = x * y; console.log(result);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output: <b>12</b>
        </div>
      </div>

      {/* Example 3: Code Blocks */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Code Blocks</h2>
        <p>
          Code blocks group multiple statements using <code>{`{}`}</code>. They
          are used in functions, loops, and conditionals.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`{
  let name = "Abhi";
  let age = 22;
  console.log(name + " is " + age + " years old.");
}`}
          />
          <TryItButton
            code={`{
  let name = "Abhi";
  let age = 22;
  console.log(name + " is " + age + " years old.");
}`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output: <b>Abhi is 22 years old.</b>
        </div>
      </div>

      {/* Example 4: Conditional Statement */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Conditional Statement</h2>
        <p>
          A <code>if</code> statement executes a block of code only if a
          condition is true.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let score = 85;
if (score > 50) {
  console.log("You passed!");
} else {
  console.log("Try again!");
}`}
          />
          <TryItButton
            code={`let score = 85;
if (score > 50) {
  console.log("You passed!");
} else {
  console.log("Try again!");
}`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output: <b>You passed!</b>
        </div>
      </div>

      {/* Example 5: Loop Statement */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Loop Statement</h2>
        <p>
          Loops repeat a block of statements multiple times until a condition is
          met.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`for (let i = 1; i <= 3; i++) {
  console.log("Count: " + i);
}`}
          />
          <TryItButton
            code={`for (let i = 1; i <= 3; i++) {
  console.log("Count: " + i);
}`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #2563eb",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Count: 1
            <br />
            Count: 2
            <br />
            Count: 3
          </pre>
        </div>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Always end statements with a semicolon (<code>;</code>).
          </li>
          <li>Keep statements short and clear.</li>
          <li>Group related statements inside code blocks.</li>
          <li>Use indentation to make code more readable.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>Expressions</b> and how they combine with statements.
          </li>
          <li>
            Explore <b>Conditional</b> and <b>Looping</b> statements in depth.
          </li>
          <li>
            Try writing a <b>mini program</b> using multiple types of
            statements.
          </li>
        </ul>
      </div>
    </div>
  );
}
